import { getNewToken } from './axiosRequest';

/**
 * Manages the tokens for createAuthProvider
 */
export const createTokenProvider = () => {
  /**
   * @param jwtToken JSON Token
   * @returns Expiry time or null if token doesn't exist
   */
  const getExpirationDate = (jwtToken?: string): number | null => {
    if (!jwtToken) return null;
    const jwt = JSON.parse(atob(jwtToken.split('.')[1]));

    // multiply by 1000 to convert seconds into milliseconds
    return (jwt && jwt.exp && jwt.exp * 1000) || null;
  };

  /**
   * Refresh the access token a bit before it actually expires so an in-flight
   * request never goes out with a token the server will reject.
   */
  const EXPIRY_BUFFER_MS = 60 * 1000;

  /**
   * @param exp Expiry Date
   * @returns boolean Whether expiry date has passed (within the buffer) or not
   */
  const isExpired = (exp?: number | null) => {
    if (!exp) return false;
    return Date.now() > exp - EXPIRY_BUFFER_MS;
  };

  /**
   * Holds the in-flight refresh so that several requests firing at once while
   * the token is expired only trigger a single /auth/refresh_token call
   * (single-flight) instead of racing each other.
   */
  let refreshPromise: Promise<string | null> | null = null;

  const refreshAccessToken = (): Promise<string | null> => {
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          const response = await getNewToken();
          if (response && response.goodStatus) {
            const { accessToken: newToken } = response.data;
            setToken(newToken);
            return newToken;
          }
          // Refresh failed (e.g. refresh token expired). Keep the stale token
          // in storage so route guards don't kick the user off the page; the
          // request layer will prompt for re-login via the modal instead.
          return null;
        } catch {
          return null;
        } finally {
          refreshPromise = null;
        }
      })();
    }
    return refreshPromise;
  };

  /**
   * @returns string Token stored by token provider
   */
  const getToken = async () => {
    let _accessToken: string | null =
      JSON.parse(localStorage.getItem('AUTH_TOKEN') || 'null') || null;
    if (!_accessToken) {
      return null;
    }
    if (isExpired(getExpirationDate(_accessToken))) {
      _accessToken = await refreshAccessToken();
    }

    return _accessToken;
  };

  const isLoggedIn = () => {
    let _accessToken: string | null =
      JSON.parse(localStorage.getItem('AUTH_TOKEN') || 'null') || null;
    return !!_accessToken;
  };

  let observers: Array<(isLogged: boolean) => void> = [];

  const subscribe = (observer: (isLogged: boolean) => void) => {
    observers.push(observer);
  };

  const unsubscribe = (observer: (isLogged: boolean) => void) => {
    observers = observers.filter((_observer) => _observer !== observer);
  };

  const notify = () => {
    const isLogged = isLoggedIn();
    observers.forEach((observer) => observer(isLogged));
  };

  /**
   * @param token Access token that will be used by createTokenProvider
   */
  const setToken = (token: string | null) => {
    if (token) {
      localStorage.setItem('AUTH_TOKEN', JSON.stringify(token));
    } else {
      localStorage.removeItem('AUTH_TOKEN');
    }
    notify();
  };

  return {
    getToken,
    isLoggedIn,
    setToken,
    subscribe,
    unsubscribe,
  };
};
